import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QrCode, Copy, CheckCircle2, Loader2, Dumbbell, ShieldCheck } from "lucide-react";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png";

export default function PayLink() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [pix, setPix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/paylink/${token}`);
      setInfo(data);
      if (data.paylink.status === "paid") setPaid(true);
    } catch { toast.error("Link inválido ou expirado"); }
  };
  useEffect(() => { load(); }, [token]);

  // Poll status
  useEffect(() => {
    if (!pix?.session_id || paid) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/pix/status/${pix.session_id}`);
        if (data.payment_status === "paid") { setPaid(true); toast.success("Pagamento confirmado!"); }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [pix, paid]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/paylink/${token}/pix`);
      if (data.already_paid) { setPaid(true); return; }
      setPix(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const confirmDemo = async () => {
    try { await api.post(`/paylink/${token}/confirm-demo`); setPaid(true); toast.success("Pagamento simulado (demo)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (!info) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Carregando...</div>;

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 bg-neutral-900 border-neutral-800 text-white">
        <div className="text-center mb-6">
          <img src={LOGO} alt="American Fit" className="mx-auto max-w-[180px] mb-3"/>
          <div className="text-xs uppercase tracking-widest text-rose-400">Renovação da mensalidade</div>
        </div>

        <div className="p-4 rounded-xl bg-black/50 border border-neutral-800 mb-4">
          <div className="text-xs text-slate-400">Aluno</div>
          <div className="font-heading text-lg font-bold">{info.student?.name}</div>
          <div className="text-xs font-mono text-slate-500">{info.student?.cpf}</div>
          <div className="mt-3 pt-3 border-t border-neutral-800">
            <div className="text-xs text-slate-400">Plano {info.paylink.plan_name}</div>
            <div className="font-heading text-3xl font-extrabold text-rose-500 mt-1">R$ {(info.paylink.amount || 0).toFixed(2)}</div>
          </div>
        </div>

        {paid ? (
          <div className="text-center py-6 bg-emerald-900/30 rounded-xl border-2 border-emerald-500" data-testid="paylink-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-2"/>
            <div className="font-heading text-xl font-bold text-emerald-300">Pagamento confirmado!</div>
            <p className="text-sm text-emerald-200 mt-1">Sua mensalidade foi renovada. Bons treinos! 💪</p>
          </div>
        ) : !pix ? (
          <Button onClick={generate} disabled={loading}
            data-testid="paylink-generate-button"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando Pix...</> : <><QrCode className="w-5 h-5 mr-2"/>Gerar Pix agora</>}
          </Button>
        ) : (
          <div className="space-y-3">
            {pix.is_mock && (
              <div className="p-2 bg-amber-950/40 border border-amber-800 rounded-lg text-xs text-amber-300">
                Modo demo: Pix não ativado no Stripe. Botão abaixo simula pagamento.
              </div>
            )}
            {pix.qr_image_url && (
              <div className="flex justify-center bg-white p-4 rounded-xl">
                <img src={pix.qr_image_url} alt="QR Pix" className="w-56 h-56" data-testid="paylink-qr-image"/>
              </div>
            )}
            {pix.pix_code && (
              <div>
                <div className="text-xs font-semibold text-slate-300 mb-1">Pix Copia e Cola</div>
                <div className="flex gap-2">
                  <div className="flex-1 p-2 bg-black/50 rounded-lg text-xs font-mono break-all border border-neutral-800 max-h-24 overflow-y-auto">{pix.pix_code}</div>
                  <Button size="sm" variant="outline" onClick={()=>{navigator.clipboard.writeText(pix.pix_code); toast.success("Copiado");}}
                    className="border-neutral-700 text-white hover:bg-neutral-800"
                    data-testid="paylink-copy-button"><Copy className="w-4 h-4"/></Button>
                </div>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500"/>Aguardando pagamento...
            </div>
            {pix.is_mock && <Button size="sm" onClick={confirmDemo} data-testid="paylink-demo-confirm-button" className="w-full bg-neutral-800 hover:bg-neutral-700">Simular pagamento (demo)</Button>}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-neutral-800 flex items-center gap-2 text-xs text-slate-500 justify-center">
          <ShieldCheck className="w-3.5 h-3.5"/>Pagamento seguro processado pelo Stripe
        </div>
      </Card>
    </div>
  );
}
